import React, { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Table } from 'antd';
import { Tool } from '../../../../store/types/prescription';
import { GlobalState } from '../../../../store/reducers';
import { getPxElementConfig, getSectionNameConfig } from '../../../../store/selectors/session';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../../store/actions';
import { PatientDetailsVisits } from '../../../../store/reducers/prescriptionPad';
import { GeniePadElementsSettingItem } from '../../../../api/pushGenie';
import { PadElements } from '../../SummaryView';
import moment from 'moment';
import { AntTooltip } from '../../../common/AntTooltip';
import { getAllPadSections, checkIsEmpty, onCopy } from '../../../common/PastVisitUtils';
import PastVisitEmptyCard from './PastVisitEmptyCard';
import {
    downArrowIcon,
    filesCopyIcons,
    leftScrollButton,
    pdfIcon,
    rightScrollButton,
} from '../../../common/Icons';
import { IFrameBareDialog } from '../../../common/IFrameDialog';
import { getPxPrintUrl, isBusinessVisit } from '../../../../pages/utils';
import Hotkeys from 'react-hot-keys';
import ModalDialog from '../../../common/ModalDialog';
import { PadDForm } from '../../../../api/pushVisit';
import { ALL_PAST_VISIT } from './AllPastVisit';
import { configs } from '../../../../pages/prescriptionPad/magicBox/VisitsConfigs';
import { DEFAULT_CONFIG } from '../../../../pages/prescriptionPad/magicBox/VisitsDefaultConfig';
import Wrapper from '../../../../pages/prescriptionPad/magicBox/Wrapper';
import { SectionNameConfig } from '../../../../store/db';
import { VISIT_TYPE } from '../../../../api/fetchAllVisitsForPatient';
import { BusinessDoctorName } from '../../../../pages/prescriptionPad/magicBox/magicBoxUtils';

type Props = {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    visits?: PatientDetailsVisits;
    sectionNameConfig?: SectionNameConfig;
    padElementsConfig: GeniePadElementsSettingItem[];
    isIndividualElementCopyEnabled?: boolean;
    isWrapper?: boolean;
    onCopy: (visit: Visits, allSections: string[]) => void;
    aclClassForCopyCta?: string;
    onRefresh?: () => void;
};

const PDF_ICON_FLAVOURS = ['dw', 'an', 'io', 'ip'];

export type Visits = Tool & {
    date: string;
    visitId: string;
    precriptionId?: string;
    flavour?: string;
    type?: VISIT_TYPE;
    doc_name?: string;
};
type TableRow = {
    [key: string]: Visits | { icon?: JSX.Element; sectionName?: string };
};
type SectionDetails = { icon?: JSX.Element; sectionName?: string };

const defaultScrollValues = { x: '100%', y: '100%' };
const pdfCopyIcon = pdfIcon();

export const generatePastVisitTableCellId = (id: string) => {
    return `${id}-past-visit-table-cell`;
};

const copyIcon = filesCopyIcons();
type State = {
    iFrameId: string | undefined;
};
const pastVitalTableLocale = {
    emptyText: 'empty prescription',
};
export class PastVisitTable extends PureComponent<Props, State> {
    allSections = getAllPadSections(
        this.props.padElementsConfig,
        (this.props.visits?.data
            ?.map((visit) => visit.dyform)
            .flat()
            .filter(Boolean) as PadDForm[]) || [],
    );
    ICON_COL_WIDTH = 40;
    EMPTY_VISIT_COL_WIDTH = 220;
    VISIT_COL_WIDTH = 296;

    state: State = {
        iFrameId: undefined,
    };

    setIFrame = (visit: Visits) => {
        this.setState({ iFrameId: visit.precriptionId });
    };

    clearIFrame = () => {
        this.setState({ iFrameId: undefined });
    };

    renderTitle = (visit: Visits) => {
        const isEmpty = checkIsEmpty(this.allSections, visit);
        const { onCopy, aclClassForCopyCta } = this.props;
        const isEmptyView =
            isEmpty || !visit.flavour || !PDF_ICON_FLAVOURS.includes(visit.flavour.toLowerCase());

        return (
            <Fragment>
                <div className="p-5 flex justify-between text-13 font-700 items-start">
                    <div className="flex-1 flex items-start space-x-5">
                        {isBusinessVisit(visit) && <BusinessDoctorName docName={visit.doc_name} />}
                        <p className="text-neutral-black">
                            {moment(visit.date).format('MMM DD, YYYY')}:{' '}
                            {visit.diagnosis?.map((d) => d.name).join(', ')}
                        </p>
                    </div>
                    <div className="flex space-x-8">
                        {isEmptyView && (
                            <button
                                onClick={() => this.setIFrame(visit)}
                                className="text-12  h-max flex items-center border border-darwin-primary-500 rounded-4 space-x-6 px-8 py-2"
                            >
                                <span className="uppercase font-700 text-darwin-primary-500">
                                    pdf
                                </span>
                                <span>{pdfCopyIcon}</span>
                            </button>
                        )}
                        {!isEmpty && (
                            <AntTooltip placement="top" content={'Copy to Pad'}>
                                <span
                                    onClick={() => onCopy(visit, this.allSections)}
                                    className={`ml-4 min-h-28 min-w-28 flex justify-center items-center bg-darwin-neutral-50 rounded-4 cursor-pointer ${
                                        aclClassForCopyCta || ''
                                    }`}
                                >
                                    {copyIcon}
                                </span>
                            </AntTooltip>
                        )}
                    </div>
                </div>
            </Fragment>
        );
    };

    renderRow = (visit: Visits, row: TableRow, index: number) => {
        const { dispatch, isIndividualElementCopyEnabled = true } = this.props;
        const id = (row['col0'] as SectionDetails).sectionName;
        const config = configs[id as keyof PadElements] || DEFAULT_CONFIG;

        if (!config.content(visit, id as keyof Tool)) {
            return (
                <div id={index === 0 ? generatePastVisitTableCellId(visit.visitId) : undefined}>
                    -
                </div>
            );
        }

        return (
            <div id={index === 0 ? generatePastVisitTableCellId(visit.visitId) : undefined}>
                <Wrapper
                    iconText={undefined}
                    iconCssClass={config.icon.cssClass}
                    iconTooltipContent={''}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    content={config.content(visit, id, true, dispatch)}
                    onCopy={
                        isIndividualElementCopyEnabled
                            ? () => onCopy(id as keyof Tool, visit, this.allSections, dispatch)
                            : undefined
                    }
                    isWholeBodyCopy={false}
                />
            </div>
        );
    };

    renderIcon = (sectionDetails: SectionDetails) => {
        return sectionDetails.icon;
    };

    extractIcons = (index: number) => {
        const id = this.allSections[index];

        const config = configs[id as keyof PadElements] || DEFAULT_CONFIG;
        const iconText = config.icon.text;
        const iconCssClass = config.icon.cssClass;

        const visit = this.props?.visits?.data?.[0];

        const iconTooltipContent =
            this.props.sectionNameConfig?.[id] ||
            config.icon.tooltipContent(visit || {}, id as keyof Tool) ||
            this.props.padElementsConfig.find((ele) => ele.id === id)?.name;

        if (id === 'visitDate') {
            return <></>;
        }

        return (
            <AntTooltip placement="top" content={iconTooltipContent}>
                <p
                    className={`w-24 h-24 flex cursor-pointer justify-center items-center text-12 font-700 ${iconCssClass} rounded-full`}
                >
                    {iconText}
                </p>
            </AntTooltip>
        );
    };

    createOptions = (allSections: string[], visits: PatientDetailsVisits) => {
        if (!allSections.length || !visits?.data?.length) return null;

        const size = allSections.length;
        const visitObject = visits?.data?.reduce(
            (pre, item, i) => ({ ...pre, [`col${i + 1}`]: item }),
            {},
        );

        const tableData = [];

        for (let i = 0; i < size; i++) {
            const icon = this.extractIcons(i);
            const id = this.allSections[i];

            const data = {
                col0: {
                    icon: icon,
                    sectionName: id,
                },
                ...visitObject,
            };

            const config = configs[id as keyof PadElements] || DEFAULT_CONFIG;
            const isEmpty =
                (visits?.data?.filter((visit) => {
                    return config.content(visit || [], id as keyof Tool);
                })?.length || 0) <= 0;

            if (isEmpty && id !== 'visitDate') {
                continue;
            }

            tableData.push(data);
        }
        return tableData;
    };

    iconColumn = {
        dataIndex: 'col0',
        render: this.renderIcon,
        width: this.ICON_COL_WIDTH,
        fixed: true,
    };

    render(): ReactNode {
        const { visits, isWrapper, onRefresh } = this.props;

        if (!visits || !visits.data || visits.data.length === 0) {
            return (
                <div id={ALL_PAST_VISIT} className="h-2/3 relative w-full rounded-8">
                    <PastVisitEmptyCard fetchState={visits?.state} onRefresh={onRefresh} />
                </div>
            );
        }

        const columns = [
            this.iconColumn,
            ...(visits?.data?.map((visit, i) => ({
                dataIndex: `col${i + 1}`,
                render: this.renderRow,
                title: this.renderTitle(visit),
                width: checkIsEmpty(this.allSections, visit)
                    ? this.EMPTY_VISIT_COL_WIDTH
                    : this.VISIT_COL_WIDTH,
            })) || []),
        ];

        const options = this.createOptions(
            this.allSections,
            (visits as PatientDetailsVisits) || [],
        );

        if (!columns.length || !options) {
            return null;
        }

        const { iFrameId } = this.state;

        const Wrapper = isWrapper ? PastVisitTableWrapper : Fragment;

        return (
            <Fragment>
                {iFrameId && (
                    <Hotkeys keyName="esc" onKeyDown={this.clearIFrame}>
                        <ModalDialog>
                            <IFrameBareDialog
                                url={`/pdfjs-3/web/viewer.html?file=${getPxPrintUrl(
                                    iFrameId || '',
                                )}`}
                                id="eka_new_prescription_pad"
                                closeMe={this.clearIFrame}
                            />
                        </ModalDialog>
                    </Hotkeys>
                )}
                <Wrapper>
                    <Table
                        bordered
                        className={`past-visit-table h-auto`}
                        locale={pastVitalTableLocale}
                        style={{
                            width: columns
                                .map((t) => t.width)
                                .reduce((partialSum, a) => partialSum + a, 10),
                        }}
                        dataSource={options}
                        pagination={false}
                        columns={columns}
                        scroll={defaultScrollValues}
                        sticky={true}
                    ></Table>
                </Wrapper>
            </Fragment>
        );
    }
}

function sideScroll(element: Element, distance: number) {
    element.scrollBy({
        left: distance,
        behavior: 'smooth',
    });
}

const downArrow = downArrowIcon();
const leftScroll = leftScrollButton();
const rightScroll = rightScrollButton();

type CardState = 'expanded' | 'collapsed' | undefined;

type S = {
    cardState: CardState;
    canExpand: boolean;
    canHorizontalScroll: boolean;
};

type P = {
    children: JSX.Element;
};

export const PATIENT_OVERVIEW_CONTAINER_ID = 'PATIENT_OVERVIEW_CONTAINER_ID';

class PastVisitTableWrapper extends React.PureComponent<P, S> {
    state: S = {
        cardState: 'collapsed',
        canExpand: false,
        canHorizontalScroll: false,
    };

    container?: HTMLDivElement | null = null;
    tableRef?: HTMLDivElement | null = null;
    leftRightScrollbar?: HTMLDivElement | null = null;
    height = screen.height;

    isScrollButtonVisible = () => {
        const antTableCells = this.tableRef?.querySelector('.ant-table-body');

        if (this.leftRightScrollbar) {
            if (antTableCells?.scrollWidth === antTableCells?.clientWidth) {
                this.leftRightScrollbar.style.display = 'none';
            } else {
                this.leftRightScrollbar.style.display = 'flex';
            }
        }
    };

    changeCardState = () => {
        const { cardState } = this.state;
        if (
            (this.container?.clientHeight || 0) > (this.height * 3) / 6 &&
            cardState === undefined
        ) {
            this.setState({
                cardState: 'collapsed',
            });
        }
    };

    onLeftClick = () => {
        const container = this.tableRef?.querySelector('.ant-table-body');

        if (container) {
            sideScroll(container, -700);
        }
    };

    onRightClick = () => {
        const container = this.tableRef?.querySelector('.ant-table-body');

        if (container) {
            sideScroll(container, 700);
        }
    };

    onShowMoreClick = () => {
        this.setState({
            cardState: 'expanded',
        });
        this.setTableOverFlow('expanded');
    };

    setTableOverFlow = (cardState: CardState) => {
        const container: HTMLElement | null | undefined = this.tableRef?.querySelector(
            '.ant-table-body',
        );
        if (!container) {
            return;
        }
        container.style.overflowY = cardState === 'collapsed' ? 'hidden' : 'auto';
    };

    calculateShowMore = (): void => {
        const container: HTMLElement | null | undefined = this.tableRef?.querySelector(
            '.ant-table-body',
        );
        const panelContainer = document.getElementById(PATIENT_OVERVIEW_CONTAINER_ID);
        if (!container || !panelContainer) {
            return;
        }
        let canExpand = this.state.canExpand;
        const canHorizontalScroll = container.clientWidth !== container.scrollWidth;
        if (container?.scrollHeight > panelContainer?.clientHeight * 0.8) {
            canExpand = true;
        }
        this.setState({
            canExpand: canExpand,
            canHorizontalScroll: canHorizontalScroll,
        });

        this.setTableOverFlow(this.state.cardState);
    };

    componentDidMount = () => {
        this.calculateShowMore();
    };

    componentDidUpdate = (props: P) => {
        if (this.props !== props) {
            this.calculateShowMore();
        }
    };

    render() {
        const { cardState, canExpand, canHorizontalScroll } = this.state;
        const { children } = this.props;

        const bodyCssClass = canExpand
            ? cardState === 'collapsed'
                ? 'h-4/5 overflow-hidden'
                : 'h-full overflow-hidden'
            : '';
        const showMoreCssClass = 'showmore-fadeout select-none';

        return (
            <div
                ref={(ref) => (this.container = ref)}
                className={`${bodyCssClass} ${
                    canExpand ? 'min-h-3/5' : ''
                } relative w-full rounded-8 template-list bg-white`}
                id={ALL_PAST_VISIT}
            >
                <div
                    ref={(ref) => (this.tableRef = ref)}
                    id="past-visit-table"
                    className={`h-full ${
                        cardState === 'collapsed' ? 'overflow-hidden' : 'overflow-auto'
                    }`}
                >
                    {children}
                    {canExpand && cardState === 'collapsed' && (
                        <div className={showMoreCssClass} onClick={this.onShowMoreClick}>
                            <button className="flex items-center bg-white border border-darwin-primary-500 text-16 font-700 text-darwin-primary-500 px-16 py-5 rounded-4 space-x-8">
                                {downArrow}
                                <span>show more</span>
                            </button>
                        </div>
                    )}
                </div>
                {canHorizontalScroll && (
                    <div
                        ref={(ref) => (this.leftRightScrollbar = ref)}
                        className="flex delete-icon z-10 absolute right-0 top-2/4 items-center space-x-8 w-max mr-10"
                    >
                        <div
                            onClick={this.onLeftClick}
                            className="flex cursor-pointer justify-center items-center w-32 h-32 bg-darwin-neutral-400 hover:bg-darwin-neutral-900 rounded-full"
                        >
                            {leftScroll}
                        </div>
                        <div
                            onClick={this.onRightClick}
                            className="flex cursor-pointer justify-center items-center w-32 h-32 bg-darwin-neutral-400 hover:bg-darwin-neutral-900 rounded-full"
                        >
                            {rightScroll}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export const PastVisitTableComponent = connect((state: GlobalState) => ({
    padElementsConfig: getPxElementConfig(state),
    sectionNameConfig: getSectionNameConfig(state),
}))(PastVisitTable);
